import { InputParameterHierarchyType, InputParameterHierarchyTypeDisplayName } from '../model'
import React, { useState } from 'react'
import { OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { useZocksAuth } from '../../ScopedBlock/hooks'
import { Button } from '../../Button/Button'
import {
  InputParametersHierarchyContext,
  useDeleteNodeMutation,
  useGetTemplateReferencesByContextQuery,
  useGetTemplateReferencesForAllUsersByContextQuery,
} from '../../../store/ai/inputParametersHierarchy'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { InputParametersHierarchyLevel, InputParametersHierarchyNodeTemplateReference, TemplateReference } from '../../../models/InputParametersHierarchy'
import './TemplateSelectRow.scss'
import { useNavigate } from 'react-router-dom'
import { useTemplateSelection } from '../../../views/Setup/Templates/useTemplateSelection'
import { AllUserReferencesModal } from './AllUserReferencesModal'
import { MissingScopeAction } from '../../ScopedBlock/models'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getInContextTrackingDetails } from '../../../views/Setup/Templates/tracking'

export interface TemplateSelectRowProps {
  title?: React.ReactNode
  templateSelectionSubHeader: string
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
}

export const TemplateSelectRow: React.FC<TemplateSelectRowProps> = ({ title, templateSelectionSubHeader, type, typeId, context }) => {
  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const navigate = useNavigate()
  const { selectTemplate } = useTemplateSelection()
  const [triggerDeleteNode, { isLoading: isDeleteNodeLoading }] = useDeleteNodeMutation()
  const [showAllUserReferencesModal, setShowAllUserReferencesModal] = useState(false)
  const onOpen = (templateReference: TemplateReference) => {
    navigate(`/workspace/setup/templates/template/${templateReference.id}`)
  }
  const header = `Choose ${InputParameterHierarchyTypeDisplayName[type]} Template`

  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const accountId = useAppSelector(selectAccountId)
  const { data: userReferences, isLoading } = useGetTemplateReferencesByContextQuery({
    accountId,
    type,
    typeId,
    context,
  })
  const [globalReference, userReference] = userReferences ? extractGlobalAndUserReferences(context, userReferences.references) : [undefined, undefined]

  const onChooseUser = () => {
    selectTemplate({
      header,
      subHeader: templateSelectionSubHeader,
      type,
      typeId,
      context,
      templateId: userReference?.template?.id,
      nodeId: userReference?.id,
    })
  }

  const onRemoveUser = tracked(
    async () => {
      if (!userReference) {
        return
      }
      await triggerDeleteNode({ accountId, type, typeId, nodeId: userReference!.id! })
    },
    {
      eventName: TrackEvent.TEMPLATE_ASSIGNMENT_REMOVED,
    },
    {
      condition: !!userReference,
      eventDetailsExtractor: () => getInContextTrackingDetails(userReference!.template.id, type, typeId, context),
    },
  )

  const onChooseGlobal = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { userId, ...globalContext } = context
    selectTemplate({
      header,
      subHeader: templateSelectionSubHeader,
      type,
      typeId,
      context: globalContext,
      templateId: globalReference?.template?.id || userReferences?.default?.id,
      nodeId: globalReference?.id,
    })
  }

  return (
    <tr>
      {showAllUserReferencesModal && (
        <AllUserReferencesModal
          type={type}
          typeId={typeId}
          context={context}
          show={showAllUserReferencesModal}
          onHide={() => setShowAllUserReferencesModal(false)}
        />
      )}
      <td>{title || InputParameterHierarchyTypeDisplayName[type]}</td>
      <td>
        <UserReferenceButton
          templateReference={userReference?.template}
          onOpen={onOpen}
          onChoose={onChooseUser}
          onRemove={onRemoveUser}
          isLoading={isLoading || isDeleteNodeLoading}
        />
      </td>
      <td>
        <GlobalReferenceButton
          templateReference={globalReference?.template}
          defaultTemplateReference={userReferences?.default}
          onOpen={onOpen}
          onChoose={onChooseGlobal}
          isLoading={isLoading}
        />
      </td>
      {hasIpHierarchyAdminScope && (
        <td>
          <AllUserReferencesButton type={type} typeId={typeId} context={context} onOpen={() => setShowAllUserReferencesModal(true)} />
        </td>
      )}
    </tr>
  )
}

interface UserReferenceButtonProps {
  templateReference?: TemplateReference
  onOpen: (templateReference: TemplateReference) => void
  onChoose: () => void
  onRemove: (templateReference: TemplateReference) => void
  isLoading: boolean
}

const UserReferenceButton: React.FC<UserReferenceButtonProps> = ({ templateReference, onOpen, onChoose, onRemove, isLoading }) => {
  const [showIconButton, setShowIconButton] = useState(false)
  return templateReference ? (
    <div className='d-flex align-items-center flex-row gap-2' onMouseEnter={() => setShowIconButton(true)} onMouseLeave={() => setShowIconButton(false)}>
      <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Open template</BootstrapTooltip>}>
        <Button className='templateSelectButton button-medium' variant='grayGhost' onClick={() => onOpen(templateReference)} loading={isLoading}>
          <div className='d-flex align-items-center'>
            <i className='bi bi-file-earmark-text pe-2' />
            <span className='d-none d-sm-inline template-name'>{templateReference.name}</span>
          </div>
        </Button>
      </OverlayTrigger>
      <div className={showIconButton ? 'visible' : 'invisible'}>
        <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Remove your choice and use default</BootstrapTooltip>}>
          {({ ref, ...triggerHandler }) => (
            <ScopedBlock
              missingScopeAction={MissingScopeAction.UPGRADE}
              quota={{
                quota: 'ai.ipHierarchy.value',
              }}
            >
              <Button
                ref={ref}
                className='templateSelectButton button-medium p-2'
                variant='grayGhost'
                onClick={() => onRemove(templateReference)}
                loading={isLoading}
                disabled={isLoading}
                {...triggerHandler}
              >
                <i className='bi bi-x-lg' />
              </Button>
            </ScopedBlock>
          )}
        </OverlayTrigger>
      </div>
    </div>
  ) : (
    <ChooseButton onChoose={onChoose} isLoading={isLoading} checkQuota />
  )
}

interface GlobalReferenceButtonProps {
  templateReference?: TemplateReference
  defaultTemplateReference?: TemplateReference
  onOpen: (templateReference: TemplateReference) => void
  onChoose: (templateReference?: TemplateReference) => void
  isLoading: boolean
}

const GlobalReferenceButton: React.FC<GlobalReferenceButtonProps> = ({ templateReference, defaultTemplateReference, onOpen, onChoose, isLoading }) => {
  const { hasScope } = useZocksAuth()
  const hasIpHierarchyAdminScope = hasScope('**', 'iph:admin')
  const effectiveTemplateReference = templateReference || defaultTemplateReference
  const [showIconButton, setShowIconButton] = useState(false)

  return effectiveTemplateReference ? (
    <div className='d-flex align-items-center flex-row gap-2' onMouseEnter={() => setShowIconButton(true)} onMouseLeave={() => setShowIconButton(false)}>
      <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Open template</BootstrapTooltip>}>
        <Button className='templateSelectButton button-medium' variant='grayGhost' onClick={() => onOpen(effectiveTemplateReference)}>
          <div className='d-flex align-items-center'>
            <i className='bi bi-file-earmark-text pe-2' />
            <span className='d-none d-sm-inline template-name'>{effectiveTemplateReference.name}</span>
          </div>
        </Button>
      </OverlayTrigger>
      {hasIpHierarchyAdminScope ? (
        <div className={showIconButton ? 'visible' : 'invisible'}>
          <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Change default</BootstrapTooltip>}>
            <Button className='templateSelectButton button-medium p-2' variant='grayGhost' onClick={() => onChoose(effectiveTemplateReference)}>
              <i className='bi bi-pencil' />
            </Button>
          </OverlayTrigger>
        </div>
      ) : null}
    </div>
  ) : (
    <ChooseButton onChoose={onChoose} isLoading={isLoading} />
  )
}

interface ChooseButtonProps {
  onChoose: (templateReference?: TemplateReference) => void
  checkQuota?: boolean
  isLoading: boolean
}

const ChooseButton: React.FC<ChooseButtonProps> = ({ onChoose, checkQuota = false, isLoading }) => {
  return checkQuota ? (
    <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Choose a template to override the default</BootstrapTooltip>}>
      {({ ref, ...triggerHandler }) => (
        <ScopedBlock
          missingScopeAction={MissingScopeAction.UPGRADE}
          quota={{
            quota: 'ai.ipHierarchy.value',
          }}
        >
          <Button ref={ref} className='chooseButton button-medium' variant='grayGhost' onClick={() => onChoose()} loading={isLoading} {...triggerHandler}>
            Choose a template
          </Button>
        </ScopedBlock>
      )}
    </OverlayTrigger>
  ) : (
    <OverlayTrigger placement='top' overlay={<BootstrapTooltip>Choose a template to override the default</BootstrapTooltip>}>
      <Button className='chooseButton button-medium' variant='grayGhost' onClick={() => onChoose()} loading={isLoading}>
        Choose a template
      </Button>
    </OverlayTrigger>
  )
}

interface AllUserReferencesButtonProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  onOpen: () => void
}

const AllUserReferencesButton: React.FC<AllUserReferencesButtonProps> = ({ type, typeId, context, onOpen }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: allReferences, isLoading } = useGetTemplateReferencesForAllUsersByContextQuery({
    accountId,
    type,
    typeId,
    context,
  })

  return (
    <Button className='button-medium allUserReferencesButton' variant='borderless' onClick={onOpen} loading={isLoading}>
      {allReferences?.length ? (allReferences.length == 1 ? '1 user' : `${allReferences.length} users`) : 'Assign to users'}
    </Button>
  )
}

const extractGlobalAndUserReferences = (
  context: InputParametersHierarchyContext,
  references: { [key in InputParametersHierarchyLevel]: InputParametersHierarchyNodeTemplateReference },
): [InputParametersHierarchyNodeTemplateReference | undefined, InputParametersHierarchyNodeTemplateReference | undefined] => {
  if (context.meetingTemplateId) {
    return [references[InputParametersHierarchyLevel.MEETING_TEMPLATE_GLOBAL], references[InputParametersHierarchyLevel.MEETING_TEMPLATE_USER]]
  } else if (context.emailTypeId) {
    return [references[InputParametersHierarchyLevel.EMAIL_TYPE_GLOBAL], references[InputParametersHierarchyLevel.EMAIL_TYPE_USER]]
  } else if (context.documentTypeId) {
    return [references[InputParametersHierarchyLevel.DOCUMENT_TYPE_GLOBAL], references[InputParametersHierarchyLevel.DOCUMENT_TYPE_USER]]
  } else {
    return [references[InputParametersHierarchyLevel.GLOBAL], references[InputParametersHierarchyLevel.USER]]
  }
}
